import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Building2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'robots'; document.head.appendChild(meta); }
    meta.content = 'noindex, follow';
    return () => { if (meta) meta.content = 'index, follow'; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await register(email, password, name);
      toast.success('Registrierung erfolgreich');
      navigate('/admin');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.';
      toast.error(detail);
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-6">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
            <Building2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-semibold text-lg tracking-tight">VerwalterHonorar.de</span>
        </div>
        <Card>
          <CardHeader className="text-center pb-4">
            <div className="mx-auto h-10 w-10 rounded-full bg-secondary flex items-center justify-center mb-2">
              <UserPlus className="h-4 w-4 text-muted-foreground" />
            </div>
            <CardTitle className="text-lg">Registrierung</CardTitle>
            <CardDescription>Erstellen Sie ein neues Konto.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reg-name">Name</Label>
                <Input id="reg-name" value={name} onChange={e => setName(e.target.value)} placeholder="Max Mustermann" autoFocus data-testid="register-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-email">E-Mail</Label>
                <Input id="reg-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@firma.de" required data-testid="register-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-pass">Passwort</Label>
                <Input id="reg-pass" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 Zeichen" required minLength={6} data-testid="register-password" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-confirm">Passwort bestätigen</Label>
                <Input id="reg-confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Passwort wiederholen" required data-testid="register-confirm" />
              </div>
              <Button type="submit" className="w-full" disabled={loading || !email || !password} data-testid="register-submit-btn">
                {loading ? 'Registrieren...' : 'Konto erstellen'}
              </Button>
              {error && (
                <p className="text-sm text-destructive text-center" data-testid="register-error">{error}</p>
              )}
            </form>
            <p className="text-center text-sm text-muted-foreground mt-4">
              Bereits registriert? <Link to="/login" className="text-primary hover:underline font-medium" data-testid="goto-login-link">Anmelden</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
